// ============================================================================
// PublicCalendar — role-aware FullCalendar grid for the homepage.
// ============================================================================
// Renders /api/events as a FullCalendar month/week/list grid with audience
// color-coding. Anonymous viewers see public events only; logged-in users
// see the audiences their role permits (the backend handles the filtering
// based on the Bearer token we send).
//
// Click on an event → calls onEventClick with the full PublicEvent, which
// MainApp wires to the existing SignupForm registration modal.
//
// Phase 3c minimum: month grid + week + list view toggle, audience colors.
// Phase 3d will layer AGC's shared-room calendar as a second view.
// ============================================================================

import { useEffect, useMemo, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import listPlugin from '@fullcalendar/list';
import interactionPlugin from '@fullcalendar/interaction';
import { api, type PublicEvent, type Audience } from '../api';
import { useAuth } from '../auth';

interface PublicCalendarProps {
  // Optional override for click behavior. Default: navigate to #event/<id>
  // which MainApp routes to EventDetailPage.
  onEventClick?: (event: PublicEvent) => void;
  // Initial FullCalendar view. Default 'dayGridMonth'. Pass 'listMonth' for
  // a single-month list, or 'listYear' to render every event in the current
  // year as a vertical list (used as a second instance at the bottom of the
  // homepage so July events show up even when viewing June).
  initialView?: 'dayGridMonth' | 'timeGridWeek' | 'listMonth' | 'listYear';
  // Hide the List option from the headerToolbar view-switcher. Defaults to
  // true on the top calendar instance — the bottom of the homepage already
  // renders a listYear, so the List button at the top is redundant.
  hideListInToolbar?: boolean;
  // Show the prev/next/today + view-switcher buttons at the top of the
  // grid. Default true. Set false for embedded uses that don't need
  // navigation (e.g. the bottom list view shadowing the calendar above).
  showToolbar?: boolean;
  // Optional caption override. Default is the role-aware "Events calendar"
  // wording; pass an empty string to hide the caption header entirely.
  caption?: string;
  // Include past events. Default true (calendar grid wants historical
  // context). For the bottom list view we pass false so only upcoming
  // events appear.
  includePast?: boolean;
  // Filter events by tag (must match at least one of these tag names).
  // Empty array or undefined = no filter.
  filterTags?: string[];
}

// Audience → FullCalendar color pair. Background fills the event block;
// border darkens it slightly so adjacent same-color blocks stay distinct.
// Hex values chosen to match the visualizer mockup ramps the user approved.
const AUDIENCE_COLORS: Record<Audience, { bg: string; border: string }> = {
  public:   { bg: '#1D9E75', border: '#0F6E56' }, // teal — open events
  amp:      { bg: '#7F77DD', border: '#534AB7' }, // purple — AMP program
  agc:      { bg: '#D85A30', border: '#993C1D' }, // coral — AGC partner
  internal: { bg: '#888780', border: '#5F5E5A' }, // gray — UIF internal
};

const AUDIENCE_LABEL: Record<Audience, string> = {
  public:   'Open',
  amp:      'AMP Producers',
  agc:      'AGC',
  internal: 'Internal',
};

// Extract HH:MM:SS from an ISO timestamp like "2026-07-13T08:30:00Z" so we can
// rebuild a per-day timestamp on the same day. Returns "08:30:00".
function isoTimeOfDay(iso: string): string {
  const m = iso.match(/T(\d{2}:\d{2}(?::\d{2})?)/);
  if (!m) return '00:00:00';
  return m[1].length === 5 ? `${m[1]}:00` : m[1];
}

export function PublicCalendar({
  onEventClick,
  initialView = 'dayGridMonth',
  showToolbar = true,
  caption,
  includePast = true,
  filterTags,
  hideListInToolbar = true,
}: PublicCalendarProps) {
  const { token, user } = useAuth();
  const [events, setEvents] = useState<PublicEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');

  // UIF admins see drafts on the calendar (with distinct styling so they
  // spot un-published events at a glance). Non-UIF callers never get drafts
  // back from the API regardless of the flag — the server enforces the
  // role check. Templates are excluded from the calendar for every role
  // since they're content holders, not bookable occurrences.
  const isUif = user?.role === 'uif';
  useEffect(() => {
    setLoading(true);
    setError('');
    api
      .events({
        includePast,
        includeDrafts: isUif,
        token: token ?? undefined,
      })
      .then((data) => {
        setEvents(data.events ?? []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message || 'Failed to load calendar.');
        setLoading(false);
      });
  }, [token, includePast, isUif]);

  // Tag filter applies after fetch. Empty filter array = show all.
  const filteredEvents = useMemo(() => {
    if (!filterTags || filterTags.length === 0) return events;
    const wanted = new Set(filterTags.map((t) => t.toLowerCase()));
    return events.filter((ev) => {
      const evTags = ev.tags ?? [];
      return evTags.some((t) => wanted.has(t.toLowerCase()));
    });
  }, [events, filterTags]);

  // Audiences actually present in the filtered set — drives the legend.
  const audiencesInView = useMemo(() => {
    const set = new Set<Audience>();
    filteredEvents.forEach((e) => set.add((e.audience as Audience) ?? 'public'));
    return Array.from(set);
  }, [filteredEvents]);

  // Whether the rendered view includes any drafts — controls the Draft
  // legend swatch. Only ever true for UIF, since other roles can't load drafts.
  const hasDrafts = useMemo(
    () => filteredEvents.some((e) => e.is_draft),
    [filteredEvents],
  );

  const fcEvents = useMemo(() => {
    const out: any[] = [];
    const isList = initialView === 'listMonth' || initialView === 'listYear';
    for (const ev of filteredEvents) {
      const aud: Audience = (ev.audience as Audience) ?? 'public';
      const colors = AUDIENCE_COLORS[aud];
      const startDate = new Date(ev.start_date);
      const endDate = new Date(ev.end_date);
      const isMultiDay =
        startDate.getUTCFullYear() !== endDate.getUTCFullYear() ||
        startDate.getUTCMonth() !== endDate.getUTCMonth() ||
        startDate.getUTCDate() !== endDate.getUTCDate();
      const baseTitle =
        isList && isMultiDay
          ? `${ev.title} (through ${endDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })})`
          : ev.title;
      // Draft prefix only when the viewer is UIF — that's the only role that
      // ever sees drafts, so we tag the title to make them unmissable.
      const title = ev.is_draft ? `Draft · ${baseTitle}` : baseTitle;
      const common = {
        backgroundColor: colors.bg,
        borderColor: colors.border,
        textColor: '#ffffff',
        extendedProps: { ...ev, _audience: aud },
      };

      // Task #45: multi-day events should NOT render as one continuous
      // overnight-spanning block in week/day view. When per-day rows exist
      // (event.days), emit one FullCalendar event per day using each day's
      // own start_time/end_time. List view keeps its existing single-entry
      // dedup so list rows stay readable.
      const days = ev.days ?? [];
      const hasDayDetails = days.length > 0;

      if (isList) {
        // List view: one entry collapsed to start (existing behavior).
        out.push({
          ...common,
          id: String(ev.event_id),
          title,
          start: ev.start_date,
          end: ev.start_date,
        });
      } else if (isMultiDay && hasDayDetails) {
        // Multi-day grid view with per-day details — split into N events.
        // Each block uses the day's own time window so overnight gaps
        // render correctly.
        const fallbackStartHHMM = isoTimeOfDay(ev.start_date);
        const fallbackEndHHMM = isoTimeOfDay(ev.end_date);
        for (const d of days) {
          const startHHMM = (d.start_time ?? fallbackStartHHMM).slice(0, 8);
          const endHHMM   = (d.end_time   ?? fallbackEndHHMM).slice(0, 8);
          out.push({
            ...common,
            id: `${ev.event_id}-day-${d.day_index}`,
            title: days.length > 1 ? `${title} (Day ${d.day_index})` : title,
            start: `${d.day_date}T${startHHMM}Z`,
            end:   `${d.day_date}T${endHHMM}Z`,
          });
        }
      } else {
        // Single-day OR multi-day legacy without day rows — render as before.
        out.push({
          ...common,
          id: String(ev.event_id),
          title,
          start: ev.start_date,
          end: ev.end_date,
        });
      }
    }
    return out;
  }, [filteredEvents, initialView]);

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 text-center text-gray-500">
        Loading calendar…
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-4 text-center">
        {error}
      </div>
    );
  }

  // Default caption only when not explicitly overridden (empty string hides).
  const resolvedCaption =
    caption !== undefined
      ? caption
      : user
        ? `Events calendar · signed in as ${user.display_name ?? user.username}`
        : 'Upcoming workshops';

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      {(resolvedCaption || audiencesInView.length > 0) && (
        <div className="mb-3 flex items-center justify-between flex-wrap gap-2">
          {resolvedCaption ? (
            <h2 className="text-lg font-semibold text-uif-green-dark">{resolvedCaption}</h2>
          ) : (
            <span />
          )}
          <div className="flex items-center gap-3 text-xs text-gray-600 flex-wrap">
            {audiencesInView.map((aud) => (
              <span key={aud} className="inline-flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  style={{ background: AUDIENCE_COLORS[aud].bg }}
                  className="inline-block w-3 h-3 rounded-sm"
                />
                {AUDIENCE_LABEL[aud]}
              </span>
            ))}
            {hasDrafts && (
              <span className="inline-flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  style={{
                    background:
                      'repeating-linear-gradient(45deg, #888780 0 4px, #c0c0bb 4px 8px)',
                    border: '1px dashed #5F5E5A',
                  }}
                  className="inline-block w-3 h-3 rounded-sm"
                />
                Draft (preview)
              </span>
            )}
          </div>
        </div>
      )}

      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin]}
        initialView={initialView}
        // timeZone='UTC' is critical: Postgres stores workshop times as naive
        // timestamps treated semantically as Central time (8:30 AM CDT) but
        // serializes them with a UTC Z marker. Without this, FullCalendar
        // shifts them by 5 hours and displays "3:30 AM" instead of "8:30 AM".
        // Matches the toLocaleTimeString(..., {timeZone:'UTC'}) trick used
        // by WorkshopCard. See CHEATSHEET § 10 "Time zone display".
        timeZone="UTC"
        headerToolbar={
          showToolbar
            ? {
                left: 'prev,next today',
                center: 'title',
                right: hideListInToolbar
                  ? 'dayGridMonth,timeGridWeek'
                  : 'dayGridMonth,timeGridWeek,listMonth',
              }
            : false
        }
        buttonText={{
          today: 'Today',
          month: 'Month',
          week:  'Week',
          list:  'List',
        }}
        events={fcEvents}
        eventClick={(info) => {
          info.jsEvent.preventDefault();
          const ev = info.event.extendedProps as PublicEvent;
          if (onEventClick) {
            onEventClick(ev);
          } else {
            window.location.hash = `#event/${ev.event_id}`;
          }
        }}
        // Native browser tooltip on hover so the full title (and the time)
        // is readable even when the month-view block crops the visible text.
        // Drafts get a dashed border + reduced opacity so they read as
        // visually-distinct from live events without sacrificing audience
        // color recognition.
        eventDidMount={(info) => {
          const ev = info.event.extendedProps as PublicEvent;
          const startStr = new Date(ev.start_date).toLocaleString('en-US', {
            month: 'short', day: 'numeric', year: 'numeric',
            hour: 'numeric', minute: '2-digit',
            timeZone: 'UTC',
          });
          const tooltipPrefix = ev.is_draft ? 'DRAFT (not public)\n' : '';
          info.el.setAttribute('title', `${tooltipPrefix}${ev.title}\n${startStr}`);
          if (ev.is_draft) {
            info.el.style.opacity = '0.65';
            info.el.style.borderStyle = 'dashed';
            info.el.style.borderWidth = '2px';
            info.el.style.backgroundImage =
              'repeating-linear-gradient(45deg, rgba(255,255,255,0.18) 0 6px, transparent 6px 12px)';
          }
        }}
        height="auto"
        firstDay={0}
        eventTimeFormat={{
          hour: 'numeric',
          minute: '2-digit',
          meridiem: 'short',
        }}
        dayMaxEvents={3}
        nowIndicator
      />

      {events.length === 0 && (
        <p className="text-center text-gray-500 mt-4">No upcoming events.</p>
      )}
    </div>
  );
}
